import {
  CrossPieceType,
  DoorHandleType,
  DoorHole,
  DrawerHandleType,
  Material,
  MINIMUM_LARGE_DOORS_HEIGHT,
  PaneMaterial,
  PlateType,
  Position,
  PositionAngle,
  PositionLargeDoor,
  THICKNESS_PLATE_DEFAULT,
} from '../constants';
import { Color } from '../entities/Color';
import { CustomPiece } from '../pieces/CustomPiece';
import { Door } from '../pieces/Door';
import { getPiecesData } from './dataStorage';
import { Drawer } from '../pieces/Drawer';
import { CrossPiece } from '../pieces/CrossPiece';
import { Plate } from '../pieces/Plate';
import { Pane } from '../pieces/Pane';
import { DrawerSlat } from '../pieces/DrawerSlat';
import { DoorHandle } from '../pieces/DoorHandle';
import { DrawerHandle } from '../pieces/DrawerHandle';
import { PositionPiece } from '../type';

export function findDoor(
  position: PositionAngle,
  oldItem: CustomPiece<Door>,
  height?: number | null,
  width?: number | null,
  material?: PaneMaterial | null,
  hole?: DoorHole | null,
  color?: Color | null,
  slatMaterial?: Material | null,
  largeDoorPosition?: PositionLargeDoor | null,
) {
  const doorHeight = height ?? oldItem.height;
  const doorWidth = width ?? oldItem.width;
  const doorMaterial = material ?? oldItem.base.material;
  let doorHole = hole ?? oldItem.base.hole;
  const doorcolor = color ?? oldItem.color;
  let doorSlatMaterial = null;
  let doorlargeDoorPosition = null;
  if (doorHeight >= MINIMUM_LARGE_DOORS_HEIGHT) {
    doorHole = DoorHole.nothing;
    if (slatMaterial) {
      doorSlatMaterial = slatMaterial;
    } else {
      if (oldItem.base.slatMaterial) {
        doorSlatMaterial = oldItem.base.slatMaterial;
      } else {
        doorSlatMaterial = Material.Wood;
      }
    }

    if (largeDoorPosition) {
      doorlargeDoorPosition = largeDoorPosition;
    } else {
      if (oldItem.base.largeDoorPosition) {
        doorlargeDoorPosition = oldItem.base.largeDoorPosition;
      } else {
        // Be sure to always set this param if large door to avoid default value
        doorlargeDoorPosition = PositionLargeDoor.Inside;
      }
    }
  } else {
    if (
      oldItem.base.hole === 0 &&
      doorMaterial !== PaneMaterial.Glass &&
      doorMaterial !== PaneMaterial.Mirror
    ) {
      doorHole = DoorHole.cupLarge;
    }
  }

  const searchInfo = Door.getSearchInfos(
    doorHeight,
    doorWidth,
    doorMaterial,
    doorHole,
    doorcolor,
    doorSlatMaterial,
    doorlargeDoorPosition,
  );

  return getPiecesData().doors.getCustom(searchInfo, position);
}

export function findDrawer(
  position: number,
  drawer: CustomPiece<Drawer>,
  height?: number | null,
  width?: number | null,
  depth?: number | null,
  color?: Color | null,
) {
  const drawerHeight = height ?? drawer.height;
  const drawerWidth = width ?? drawer.width;
  const drawerDepth = depth ?? drawer.depth;
  const drawerColor = color ?? drawer.color;
  const searchInfo = Drawer.getSearchInfos(
    drawerHeight,
    drawerWidth,
    drawerDepth,
    drawerColor,
  );
  const pieces = getPiecesData();
  return pieces.drawers.getCustom(searchInfo, position, true);
}

export function findDrawerSlat(width: number) {
  const searchInfo = DrawerSlat.getSearchInfos(width);
  const pieces = getPiecesData();
  return pieces.drawersSlat.getCustom(searchInfo, Position.Front);
}

export function findCrossPiece(
  position: Position,
  crossPiece: CustomPiece<CrossPiece>,
  width?: number,
  material?: Material,
  type?: CrossPieceType,
  reinforce?: boolean,
  rod?: boolean,
  drill?: boolean,
) {
  const cpWidth = width ?? crossPiece.width;
  const cpMaterial = material ?? crossPiece.base.material;
  const cpType = type ?? crossPiece.base.type;
  const cpReinforce = reinforce ?? crossPiece.base.reinforce;
  const cpRod = rod ?? crossPiece.base.rod;
  const cpDrill = drill ?? crossPiece.base.drill;
  const searchInfo = CrossPiece.getSearchInfos(
    cpWidth,
    cpMaterial,
    cpType,
    cpReinforce,
    cpRod,
    cpDrill,
  );
  const pieces = getPiecesData();
  return pieces.crossPieces.getCustom(searchInfo, position);
}

export function findPlate(
  plate: CustomPiece<Plate>,
  depth?: number | null,
  width?: number | null,
  material?: PaneMaterial | null,
  color?: Color | null,
  type?: PlateType,
  thickness?: number,
) {
  const pDepth = depth ?? plate.height;
  const pWidth = width ?? plate.width;
  const pMaterial = material ?? plate.base.material;
  const pColor = color ?? plate.color;
  const pType = type ?? plate.plateType;
  const pThickness = thickness ?? THICKNESS_PLATE_DEFAULT;

  if (!pType) throw new Error('The plate should have a type');

  const searchInfo = Plate.getSearchInfos(
    pDepth,
    pWidth,
    pMaterial,
    pColor,
    pType,
    pThickness,
  );
  const pieces = getPiecesData();
  return pieces.plates.getCustom(searchInfo, Position.Front, false, pType);
}

export function findPane(
  position: Position,
  pane: CustomPiece<Pane>,
  height?: number | null,
  width?: number | null,
  material?: PaneMaterial | null,
  color?: Color | null,
) {
  const paneHeight = height ?? pane.height;
  const paneWidth = width ?? pane.width;
  const paneMaterial = material ?? pane.base.material;
  const paneColor = color ?? pane.color;
  const searchInfo = Pane.getSearchInfos(
    paneHeight,
    paneWidth,
    paneMaterial,
    paneColor,
  );
  return getPiecesData().panes.getCustom(searchInfo, position);
}

export function findDoorHandle(type: DoorHandleType, color?: Color) {
  const searchInfos = DoorHandle.getSearchInfos(type, color);
  const pieces = getPiecesData();
  return pieces.doorsHandles.getFromProps(searchInfos.key);
}

export function findDrawerHandle(
  position: PositionPiece,
  type: DrawerHandleType,
  color: Color,
) {
  const searchInfos = DrawerHandle.getSearchInfos(type, color);
  const pieces = getPiecesData();
  return pieces.drawersHandles.getCustom(searchInfos, position);
}
